import { useToastContext } from '#pplx/frontend/aether/toasts/context/useToastContext.js';
import { ApiClientsError } from '#pplx/frontend/api-clients/core/errors.js';
import { getQueryTimeoutMs } from '#pplx/frontend/api-clients/core/utils.js';
import { useSeatPaymentAuthentication } from '#pplx/frontend/apps/ask/organization/hooks/useSeatPaymentAuthentication.js';
import { getSeatChangeErrorMessage } from '#pplx/frontend/apps/ask/organization/utils/seatChangeError.js';
import { getOrganizationMembersQueryKey, getSeatInfoQueryKey, } from '#pplx/frontend/apps/ask/queries/query-keys/enterpriseKeys.js';
import { QueryClientHelper } from '#pplx/frontend/react-query/QueryClientHelper.js';
import { useQuery } from '#pplx/frontend/react-query/useQuery.js';
import { normalizeEnterpriseTierPreview } from '#pplx/frontend/user-info/enterpriseResponse.js';
import { enterpriseClient } from '#pplx_generated/typespec/rest/enterprise/client.js';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
export const useOrganizationMemberUpdateSubscriptionTier = ({ reason, onSuccess, onError, onSettled, }) => {
    const queryKey = getOrganizationMembersQueryKey();
    const queryClient = useQueryClient();
    const authenticatePayment = useSeatPaymentAuthentication();
    const { openToast } = useToastContext();
    const { $t } = useIntl();
    const getTierUpdateFallbackMessage = (error) => {
        if (error instanceof ApiClientsError &&
            error.status === 409 &&
            error.detail?.error_code === 'seat_capacity_stale') {
            return $t({
                defaultMessage: 'Seat or price details changed. Try again.',
                description: 'Error toast shown when a covered member tier seat preview becomes stale',
                id: 'bfb71c0c9b',
            });
        }
        if (error instanceof ApiClientsError && error.detail?.message != null) {
            return error.detail.message;
        }
        return $t({
            description: 'Error toast message shown when subscription tier update fails',
            defaultMessage: 'Failed to update subscription tier',
            id: 'a25aaff47b',
        });
    };
    return useMutation({
        mutationFn: async ({ orgUserUUID, tier, billingInterval, disablePremiumSecure, seatCapacityPreview, }) => {
            return authenticatePayment(seatCapacityPreview, async (paymentPreview) => {
                const { data, error, response } = await enterpriseClient.POST('/rest/enterprise/organization/user/subscription-tier-update', reason, {
                    body: {
                        org_user_uuid: orgUserUUID,
                        subscription_tier: tier,
                        subscription_billing_interval: billingInterval,
                        disable_premium_secure: disablePremiumSecure,
                        seat_capacity_preview: paymentPreview,
                    },
                    timeoutMs: getQueryTimeoutMs({ productionMs: 5000 }),
                });
                if (error?.detail?.error_code === 'already_has_subscription_tier') {
                    return {
                        status: 'success',
                    };
                }
                if (error) {
                    throw new ApiClientsError('API_CLIENTS_ERROR', {
                        cause: error,
                        status: response.status ?? 0,
                    });
                }
                return data;
            });
        },
        onSuccess: async (data) => {
            const memberRefetch = queryClient.invalidateQueries({ queryKey });
            void queryClient.invalidateQueries({ queryKey: getSeatInfoQueryKey() });
            await memberRefetch;
            openToast({
                message: $t({
                    description: 'Success toast message shown when subscription tier is updated successfully',
                    defaultMessage: 'Subscription tier updated',
                    id: '98292d592a',
                }),
                variant: 'success',
                timeoutSeconds: 5,
            });
            onSuccess?.(data);
        },
        onError: async (error) => {
            if (await onError?.(error))
                return;
            const seatErrorMessage = getSeatChangeErrorMessage(error);
            openToast({
                message: seatErrorMessage ? $t(seatErrorMessage) : getTierUpdateFallbackMessage(error),
                ...(seatErrorMessage ? { messageLineClamp: 6 } : {}),
                variant: 'error',
                timeoutSeconds: 5,
            });
        },
        onSettled: (data, error) => {
            onSettled?.(data, error);
        },
    });
};
export const useOrganizationMemberUpdateSubscriptionTierPreview = ({ reason, orgUserUUID, tier, enabled = true, }) => {
    const query = useQuery({
        queryKey: QueryClientHelper.makeEphemeralQueryKey('/rest/enterprise/organization/user/subscription-tier-update-preview', orgUserUUID, tier),
        queryFn: () => fetchOrganizationMemberUpdateSubscriptionTierPreview({ reason, orgUserUUID, tier }),
        enabled,
        gcTime: 0,
        meta: { skipPersistence: true },
        staleTime: 0,
    });
    return query;
};
export const fetchOrganizationMemberUpdateSubscriptionTierPreview = async ({ reason, orgUserUUID, tier, }) => {
    const { data, error, response } = await enterpriseClient.POST('/rest/enterprise/organization/user/subscription-tier-update-preview', reason, {
        body: {
            org_user_uuid: orgUserUUID,
            subscription_tier: tier,
        },
        timeoutMs: getQueryTimeoutMs({ productionMs: 5000 }),
    });
    if (error) {
        throw new ApiClientsError('API_CLIENTS_ERROR', {
            cause: error,
            status: response.status ?? 0,
        });
    }
    return normalizeEnterpriseTierPreview(data);
};
//# sourceMappingURL=useOrganizationMemberUpdateSubscriptionTier.js.map